import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Lock, ArrowLeft, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Navbar from '../components/layout/Navbar';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('form'); // form, success, error

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('auth.resetPassword.mismatch') || 'Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error(t('auth.resetPassword.tooShort') || 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setStatus('success');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.resetPassword.error') || 'Failed to reset password');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <CardTitle className="text-red-600">{t('auth.resetPassword.noToken') || 'Invalid Link'}</CardTitle>
              <CardDescription>
                {t('auth.resetPassword.noTokenDesc') || 'This password reset link is invalid or has expired.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Button asChild>
                <Link to="/forgot-password">
                  {t('auth.resetPassword.requestNew') || 'Request New Link'}
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {status === 'form' && (
              <>
                <Lock className="h-12 w-12 text-primary mx-auto mb-4" />
                <CardTitle>{t('auth.resetPassword.title') || 'Reset Password'}</CardTitle>
                <CardDescription>
                  {t('auth.resetPassword.description') || 'Enter your new password below'}
                </CardDescription>
              </>
            )}
            {status === 'success' && (
              <>
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <CardTitle className="text-green-600">{t('auth.resetPassword.success') || 'Password Reset!'}</CardTitle>
                <CardDescription>
                  {t('auth.resetPassword.successDesc') || 'Your password has been successfully reset. You can now login with your new password.'}
                </CardDescription>
              </>
            )}
            {status === 'error' && (
              <>
                <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <CardTitle className="text-red-600">{t('auth.resetPassword.errorTitle') || 'Reset Failed'}</CardTitle>
                <CardDescription>
                  {t('auth.resetPassword.errorDesc') || 'The reset link may have expired. Please request a new one.'}
                </CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            {status === 'form' && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">{t('auth.resetPassword.newPassword') || 'New Password'}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="reset-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('auth.resetPassword.confirmPassword') || 'Confirm Password'}</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="reset-confirm-password-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="reset-password-submit">
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.loading') || 'Loading...'}
                    </>
                  ) : (
                    t('auth.resetPassword.submit') || 'Reset Password'
                  )}
                </Button>
              </form>
            )}
            
            {status === 'success' && (
              <Button className="w-full" onClick={() => navigate('/login')}>
                {t('auth.resetPassword.goToLogin') || 'Go to Login'}
              </Button>
            )}
            
            {status === 'error' && (
              <Button variant="outline" className="w-full" asChild>
                <Link to="/forgot-password">
                  {t('auth.resetPassword.requestNew') || 'Request New Link'}
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
